import { Box } from "@mui/material";
import clsx from "clsx";
import { observer } from "mobx-react-lite";
import { ReactNode, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { DesignEditorContext } from "../../../DesignEditor/contexts/DesignEditor";
import LoadingPlaceholder from "../../../components/common/LoadingPlaceholder/LoadingPlaceholder.tsx";
import Selectable from "../../../components/common/Selectable/Selectable";
import { APPS } from "../../../constants/AppsConstants.ts";
import { MAX_FILES_LIMIT } from "../../../constants/ImageToImageConstants.ts";
import { LocalStorageConstants } from "../../../constants/LocalStorageConstants.ts";
import RouterConstants from "../../../constants/RouterConstants.ts";
import { SESSION_STORAGE_KEYS, ssGet, ssSet } from "../../../helpers/sessionStorage.ts";
import { useAppStore } from "../../../hooks/useStores";
import { ImageEditingViewMode } from "../../../models/image-to-image.ts";
import { isAutomotiveApp } from "../../../utils/index.ts";
import { showErrorToast } from "../../../utils/toast";
import iframeStore from "../../IframeNew/iframe-store.tsx";
import TgImageGenerationResult from "../../NewTG/App/Image/Result/TgImageGenerationResult.tsx";
import {
	getTgEvaluationZeroPositiveFeedbackReminderTour,
	tgEvaluationTourRefs,
} from "../../NewTG/Models/Viewer/Evaluation/tgEvaluationTourSteps.tsx";
import PlaygroundActionsHeader from "../ActionsHeader/PlaygroundActionsHeader";
import ImageToImageResult from "../ImageToImage/Results/ImageToImageResult";
import NewCollectionResult from "../ImageToImage/Results/New/NewCollectionResult";
import UploadedOrSavedResult from "../ImageToImage/Results/Uploaded/UploadedOrSavedResult";
import SingleImageToImage from "../ImageToImage/SingleImageToImage/SingleImageToImage.tsx";
import ProductPlacementResult from "../ProductPlacement/Generator/ProductPlacementResult";
import TextToImageSearch from "../TextToImage/Generator/Search/TextToImageSearch.tsx";
import TextToImageResult from "../TextToImage/Generator/TextToImageResult";
import VideoGenerationResult from "../VideoGeneration/Result/VideoGenerationResult.tsx";
import { feedbackReminderTourRefs, getFeedbackReminderTour } from "../feedbackReminderTourSteps.tsx";
import EmptyPlayground from "./EmptyState/EmptyPlayground";
import styles from "./PlaygroundImages.module.scss";

type PlaygroundImagesProps = {
	emptyState?: ReactNode;
};

const PlaygroundImages = ({ emptyState }: PlaygroundImagesProps) => {
	const { t } = useTranslation("translation", { keyPrefix: "playground" });
	const { playgroundStore, imageToImageStore, authStore, tgStore, tourStore } = useAppStore();
	const { sessionsStore } = playgroundStore;
	const resultsRef = useRef<HTMLDivElement>(null);
	const { isPopupView } = useContext(DesignEditorContext);
	const [lastCountResults, setLastCountResult] = useState(playgroundStore.playgroundResults.length);
	const isSingleMode = playgroundStore.imageEditingViewMode === ImageEditingViewMode.SINGLE;
	const isTgEvaluation: boolean = location.pathname.includes(RouterConstants.NEW_TG.MODELS.EVALUATION.path);

	useEffect(() => {
		const results = playgroundStore.playgroundResults.length > 0 ? playgroundStore.playgroundResults : [];
		const latestResult = results[results.length - 1];
		if (!latestResult) return;

		const latestResultImages = latestResult.images;
		const latestResultVideo = latestResult.video;

		// Check if images are loaded
		let areAllLatestImagesLoaded = false;
		if (latestResultImages && latestResultImages.length > 0) {
			areAllLatestImagesLoaded = latestResultImages.every((image) =>
				playgroundStore.playGroundLoadedImages.some((loadedimage) => loadedimage.id === image.id),
			);
		}

		// Check if video is loaded (has URL and not just status_url)
		const isVideoLoaded = latestResultVideo?.src && latestResultVideo.src.length > 0;

		// Scroll if either all images are loaded OR video is loaded
		if (areAllLatestImagesLoaded || isVideoLoaded) {
			debouncedScrollBottom();
			if (areAllLatestImagesLoaded) {
				playgroundStore.clearPlayGroundLoadedImagesArray();

				if (shouldShowFeedbackReminder()) {
					showFeedbackReminder();
				}

				if (shouldShowTgEvaluationZeroPositiveFeedbackReminder()) {
					showTgEvaluationZeroPositiveFeedbackReminder();
				}
			}
		}
	}, [playgroundStore.playGroundLoadedImages, playgroundStore.playgroundResults]);

	useEffect(() => {
		if (playgroundStore.playgroundResults.length > lastCountResults) {
			debouncedScrollBottom();
		}
		setLastCountResult(playgroundStore.playgroundResults.length);
	}, [playgroundStore.playgroundResults.length]);

	useEffect(() => {
		debouncedScrollBottom();
	}, [playgroundStore.imageEditingViewMode]);

	const scrollBottom = () => {
		if (resultsRef.current) {
			resultsRef.current.scrollTo({ behavior: "smooth", top: resultsRef.current.scrollHeight });
		}
	};

	const debouncedScrollBottom = useCallback(() => {
		const timeoutId = setTimeout(scrollBottom, 100);
		return () => clearTimeout(timeoutId);
	}, []);

	const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
		e.preventDefault();
		const droppedFiles = e.dataTransfer.files;
		if (droppedFiles.length > 0) {
			const files = Array.from(droppedFiles);
			if (playgroundStore.selectedConfig === APPS.IMAGE_TO_IMAGE) {
				if (files.length > MAX_FILES_LIMIT) {
					showErrorToast(t("imageToImage.filesExceedLimit"));
					return;
				}
				imageToImageStore.handleUploadImages({ target: { files: droppedFiles }, value: "" } as any);
			}
		}
	};

	const shouldShowFeedbackReminderIframe = () => {
		const iframeFeedbackCount = localStorage.getItem(LocalStorageConstants.FEEDBACK_COUNT);
		const reminderShownThisSession = ssGet(SESSION_STORAGE_KEYS.feedbackReminderShown) === "true";
		if (reminderShownThisSession) return false;

		return !iframeFeedbackCount || +iframeFeedbackCount < 3;
	};

	const shouldShowFeedbackReminder = () => {
		if (!authStore.user) return false;
		if (isTgEvaluation && tgStore.tgModelPlaygroundStore.isTutorialActive) return true;
		else if (isTgEvaluation) return false;

		if (iframeStore.isIframe()) return shouldShowFeedbackReminderIframe();

		if (authStore.user?.settings?.hideFeedbackReminder) return false;
		const timesShown = Number(localStorage.getItem(LocalStorageConstants.FEEDBACK_COUNT)) || 0;
		return timesShown < 3 && ssGet(SESSION_STORAGE_KEYS.feedbackReminderShown) !== "true";
	};

	const showFeedbackReminder = () => {
		const resultElement = playgroundStore.lastResultImage?.element;

		// In TG evaluation: only resume tg-evaluation tour when result image is ready; never show feedback-reminder
		if (isTgEvaluation) {
			if (tourStore.activeTour?.name === "tg-evaluation" && tourStore.status === "paused" && resultElement) {
				(tgEvaluationTourRefs.resultImage as React.MutableRefObject<HTMLElement | null>).current =
					resultElement;
				tourStore.resume();
			}
			return;
		}

		// Mark reminder as shown for this playground interaction (session) if in iframe
		if (iframeStore.isIframe()) ssSet(SESSION_STORAGE_KEYS.feedbackReminderShown, "true");

		if (!tourStore.activeTour && resultElement) {
			// Set the ref for the feedback reminder tour
			(feedbackReminderTourRefs.resultImage as React.MutableRefObject<HTMLElement | null>).current =
				resultElement;
			// Start the feedback reminder tour
			tourStore.start(getFeedbackReminderTour());
		}
	};

	const shouldShowTgEvaluationZeroPositiveFeedbackReminder = () => {
		if (!authStore.user || iframeStore.isIframe()) return false;

		// Don't show during the tg-evaluation tutorial (e.g. "wait for image" step) to avoid collision
		if (isTgEvaluation && tgStore.tgModelPlaygroundStore.isTutorialActive) return false;
		return (
			tgStore.tgModelStore.feedbackReport?.positive === 0 &&
			ssGet(SESSION_STORAGE_KEYS.feedbackReminderShown) !== "true"
		);
	};

	const showTgEvaluationZeroPositiveFeedbackReminder = () => {
		const resultElement = playgroundStore.lastResultImage?.element;
		if (!resultElement) return;

		// Set the ref immediately before the timeout
		(tgEvaluationTourRefs.resultImage as React.MutableRefObject<HTMLElement | null>).current = resultElement;

		setTimeout(() => {
			// Check again to ensure conditions still apply (feedbackReport is updated when feedback is given)
			shouldShowTgEvaluationZeroPositiveFeedbackReminder() &&
				tourStore.start(getTgEvaluationZeroPositiveFeedbackReminderTour());
		}, 2000);
	};

	return (
		<LoadingPlaceholder
			isLoading={playgroundStore.selectedConfig === APPS.AUTOMOTIVE && sessionsStore.loadingSession}
			className={styles.loadingPlaceholder}
		>
			<Box className={styles.root}>
				<PlaygroundActionsHeader />
				{playgroundStore.selectedConfig === APPS.PRODUCT_PLACEMENT ? (
					<Box className={styles.results}>
						{playgroundStore.playgroundResults.map((result, index) => (
							<ProductPlacementResult key={index} playgroundResult={result} rowIndex={index} />
						))}
					</Box>
				) : (
					<Selectable
						selectoRef={playgroundStore.selectoRef as any}
						selectableTargets={[".selectableImage"]}
						onSelect={(e) => playgroundStore.handleSelectImagesElements(e.selected, isPopupView)}
						innerScrollRef={resultsRef}
						className={styles.selectable}
						type={isSingleMode ? "single" : "multi"}
						disabled={playgroundStore.disableSelecto}
					>
						<Box
							ref={resultsRef}
							className={isSingleMode ? styles.singleResult : `results ${styles.results}`}
							onDrop={handleDrop}
							onDragOver={(e) => e.preventDefault()}
						>
							<>
								{!playgroundStore.playgroundResults.length ? (
									emptyState || (
										<EmptyPlayground
											hideSubHeader={isAutomotiveApp()}
											header={isAutomotiveApp() ? t("automotive.uploadVehicleImages") : undefined}
										/>
									)
								) : isSingleMode ? (
									<SingleImageToImage
										swiperClassName={clsx({
											[styles.testToImageSwiper]:
												playgroundStore.selectedConfig === APPS.TEXT_TO_IMAGE,
										})}
									/>
								) : (
									playgroundStore.playgroundResults.map((result, index) => {
										// Show overlay when new tour system is targeting this result (tg-evaluation or feedback-reminder)
										const isLastResultImage =
											playgroundStore.lastResultImage?.imageId === result.images?.[0]?.id;
										const isFeedbackTour =
											tourStore.activeTour?.name === "tg-evaluation" ||
											tourStore.activeTour?.name === "feedback-reminder" ||
											tourStore.activeTour?.name ===
												"tg-evaluation-zero-positive-feedback-reminder";
										const isTourTargetingResult =
											isFeedbackTour &&
											tourStore.run &&
											tourStore.currentStep?.targetRef.current ===
												playgroundStore.lastResultImage?.element;
										const showFeedbackTutorialOverlay: boolean = !!(
											isLastResultImage && isTourTargetingResult
										);

										return result.type === APPS.TEXT_TO_IMAGE ? (
											<TextToImageResult
												key={index}
												playgroundResult={result}
												showFeedbackTutorialOverlay={showFeedbackTutorialOverlay}
											/>
										) : result.type === APPS.TG_IMAGE_GENERATION ? (
											<TgImageGenerationResult
												key={index}
												playgroundResult={result}
												showFeedbackTutorialOverlay={showFeedbackTutorialOverlay}
											/>
										) : result.type === APPS.VIDEO_GENERATION ? (
											<VideoGenerationResult key={index} playgroundResult={result} />
										) : result.type === APPS.IMAGE_TO_IMAGE || result.type === APPS.AUTOMOTIVE ? (
											<ImageToImageResult
												key={index}
												playgroundResult={result}
												rowIndex={index}
												showFeedbackTutorialOverlay={showFeedbackTutorialOverlay}
											/>
										) : result.type === "upload" || result.type === "save" ? (
											<UploadedOrSavedResult
												key={index}
												playgroundResult={result}
												label={result.type}
												rowIndex={index}
												showFeedbackTutorialOverlay={showFeedbackTutorialOverlay}
											/>
										) : (
											result.type === "new" && (
												<NewCollectionResult
													key={index}
													playgroundResult={result}
													showFeedbackTutorialOverlay={showFeedbackTutorialOverlay}
												/>
											)
										);
									})
								)}
							</>
						</Box>
						{playgroundStore.selectedConfig === APPS.TEXT_TO_IMAGE && <TextToImageSearch />}
					</Selectable>
				)}
			</Box>
		</LoadingPlaceholder>
	);
};

export default observer(PlaygroundImages);
